import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Upload, Download, Trash2, Edit, AlertCircle, CheckCircle, X, Folder, AlertTriangle, Eye } from 'lucide-react';
import FolderSelector from '../components/FolderSelector';
import FilePreviewPane from '../../components/FilePreviewPane';

export default function FilesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState<any[]>([]);
  const [folders, setFolders] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, { status: 'pending' | 'uploading' | 'success' | 'error'; message?: string }>>({});
  const [description, setDescription] = useState('');
  const [tier, setTier] = useState(-1);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [filterFolder, setFilterFolder] = useState(searchParams.get('folder') || '');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingFile, setEditingFile] = useState<any | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editTier, setEditTier] = useState(-1);
  const [editFolder, setEditFolder] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filesPerPage, setFilesPerPage] = useState(20);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'downloads'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; fileId: string | null }>({
    show: false,
    fileId: null,
  });
  const [previewFile, setPreviewFile] = useState<any | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreviewClick = useCallback(async (file: any) => {
    setPreviewFile(file);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const url = await api.getFileAdminPreviewUrl(file._id);
      setPreviewUrl(url || null);
    } catch {
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    // Read folder from URL params on mount
    const folderParam = searchParams.get('folder');
    if (folderParam) {
      setFilterFolder(folderParam);
    }
  }, [searchParams]);

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    loadFiles();
  }, [currentPage, filesPerPage, filterFolder]);

  const loadFolders = async () => {
    try {
      const response = await api.getFolders();
      if (response.success) {
        setFolders(response.data);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const loadFiles = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page: currentPage,
        limit: filesPerPage,
      };
      if (filterFolder) {
        params.folder = filterFolder;
      }
      const response = await api.getFiles(params);
      if (response.success) {
        setFiles(response.data.files);
        if (response.data.pagination) {
          setTotalFiles(response.data.pagination.total);
          setTotalPages(response.data.pagination.pages);
        }
      }
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter and sort files client-side
  const getDisplayedFiles = () => {
    let filtered = [...files];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (file) =>
          file.originalName?.toLowerCase().includes(query) ||
          file.description?.toLowerCase().includes(query) ||
          file.fileType?.toLowerCase().includes(query)
      );
    }

    // Sort files
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.originalName || '').localeCompare(b.originalName || '');
          break;
        case 'date':
          comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
        case 'downloads':
          comparison = (a.downloads || 0) - (b.downloads || 0);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setSelectedFiles(filesArray);
      // Initialize upload progress for all files
      const progress: Record<string, { status: 'pending' | 'uploading' | 'success' | 'error'; message?: string }> = {};
      filesArray.forEach(file => {
        progress[file.name] = { status: 'pending' };
      });
      setUploadProgress(progress);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0 || !description) {
      setMessage({ type: 'error', text: 'Please select at least one file and provide a description' });
      return;
    }

    setIsUploading(true);
    setMessage(null);

    // Initialize all files as uploading
    const initialProgress: Record<string, { status: 'pending' | 'uploading' | 'success' | 'error'; message?: string }> = {};
    selectedFiles.forEach(file => {
      initialProgress[file.name] = { status: 'uploading' };
    });
    setUploadProgress(initialProgress);

    // Upload all files in parallel
    const uploadPromises = selectedFiles.map(async (file) => {
      try {
        const response = await api.uploadFile(file, description, tier, selectedFolder || undefined);
        
        if (response.success) {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'success', message: 'Uploaded successfully' }
          }));
          return { success: true, fileName: file.name };
        } else {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', message: response.message || 'Upload failed' }
          }));
          return { success: false, fileName: file.name, error: response.message || 'Upload failed' };
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Failed to upload file';
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'error', message: errorMessage }
        }));
        return { success: false, fileName: file.name, error: errorMessage };
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      if (failureCount === 0) {
        setMessage({ type: 'success', text: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully!` });
      } else if (successCount === 0) {
        setMessage({ type: 'error', text: `All ${failureCount} file${failureCount > 1 ? 's' : ''} failed to upload` });
      } else {
        setMessage({ type: 'error', text: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully, ${failureCount} failed` });
      }

      // Reset form if all uploads succeeded
      if (failureCount === 0) {
        setSelectedFiles([]);
        setDescription('');
        setTier(-1);
        setSelectedFolder('');
        setUploadProgress({});
        
        // Reset file input
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }

      loadFiles();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to upload files',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (file: any) => {
    setEditingFile(file);
    setEditDescription(file.description || '');
    setEditTier(file.tier !== undefined ? file.tier : -1);
    setEditFolder(file.folder?._id || file.folder || '');
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingFile) return;

    try {
      await api.updateFile(editingFile._id, {
        description: editDescription,
        tier: editTier,
        folder: editFolder || null,
      });
      setMessage({ type: 'success', text: 'File updated successfully' });
      setEditingFile(null);
      loadFiles();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update file',
      });
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirm({ show: true, fileId: id });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.fileId) return;

    try {
      await api.deleteFile(deleteConfirm.fileId);
      setMessage({ type: 'success', text: 'File deleted successfully' });
      setDeleteConfirm({ show: false, fileId: null });
      loadFiles();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete file',
      });
      setDeleteConfirm({ show: false, fileId: null });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">File Management</h1>
        <p className="text-gray-600 mt-1">Upload and manage content files</p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-lg flex items-start ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          )}
          <p
            className={`text-sm ${
              message.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}
          >
            {message.text}
          </p>
        </div>
      )}

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload New File</h2>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <input
              id="file-input"
              type="file"
              multiple
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              disabled={isUploading}
            />
            {selectedFiles.length > 0 && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-600 font-medium">
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected:
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {selectedFiles.map((file, index) => {
                    const progress = uploadProgress[file.name];
                    const status = progress?.status || 'pending';
                    return (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-700 truncate">{file.name}</p>
                          <p className="text-gray-500">{formatFileSize(file.size)}</p>
                        </div>
                        <div className="ml-2 flex items-center gap-2">
                          {status === 'uploading' && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                          )}
                          {status === 'success' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {status === 'error' && (
                            <AlertCircle className="h-4 w-4 text-red-600" />
                          )}
                          {status === 'pending' && !isUploading && (
                            <button
                              type="button"
                              onClick={() => {
                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                setSelectedFiles(newFiles);
                                const newProgress = { ...uploadProgress };
                                delete newProgress[file.name];
                                setUploadProgress(newProgress);
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Remove file"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          {status === 'pending' && isUploading && (
                            <div className="h-4 w-4 rounded-full border-2 border-gray-300"></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {Object.values(uploadProgress).some(p => p.status === 'error' && p.message) && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(uploadProgress)
                      .filter(([_, p]) => p.status === 'error' && p.message)
                      .map(([fileName, progress]) => (
                        <p key={fileName} className="text-xs text-red-600">
                          {fileName}: {progress.message}
                        </p>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter file description..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isUploading}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Tier
              </label>
              <select
                value={tier}
                onChange={(e) => setTier(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={isUploading}
              >
                <option value={-1}>Admin Only</option>
                <option value={0}>Tier 1 ($24)</option>
                <option value={1}>Tier 2 ($50)</option>
                <option value={2}>Tier 3 ($1000)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Folder (Optional)
              </label>
              <FolderSelector
                folders={folders}
                value={selectedFolder}
                onChange={setSelectedFolder}
                disabled={isUploading}
              />
              {folders.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Create folders in the Folders page
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isUploading || selectedFiles.length === 0 || !description}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload File
              </>
            )}
          </button>
        </form>
      </div>

      {/* Files List + Preview */}
      <div className={`flex gap-6 flex-col ${previewFile ? 'lg:flex-row lg:items-start' : ''}`}>
      <div className="flex-1 min-w-0 bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Uploaded Files ({files.length})
            </h2>
            {filterFolder && (
              <p className="text-sm text-gray-600 mt-1">
                Showing files in folder: <span className="font-medium">{folders.find(f => f._id === filterFolder)?.name || 'Unknown'}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm text-gray-700 whitespace-nowrap">Filter by folder:</label>
            <select
              value={filterFolder}
              onChange={(e) => {
                setFilterFolder(e.target.value);
                // Update URL params
                if (e.target.value) {
                  setSearchParams({ folder: e.target.value });
                } else {
                  setSearchParams({});
                }
              }}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">All Folders</option>
              <option value="null">No Folder</option>
              {folders.map((folder) => (
                <option key={folder._id} value={folder._id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : files.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No files uploaded yet</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Folder
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Downloads
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getDisplayedFiles().map((file) => (
                  <tr
                    key={file._id}
                    onClick={() => handlePreviewClick(file)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {file.originalName}
                      </div>
                      <div className="text-sm text-gray-500">{file.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {file.fileType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {file.tier === -1 ? 'Admin' : `Tier ${file.tier + 1}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {file.folder ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          <Folder className="h-3 w-3" />
                          {typeof file.folder === 'object' && file.folder !== null ? file.folder.name : 'Unknown'}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {file.downloads}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePreviewClick(file)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Preview file"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(file)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit file"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(file._id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          title="Delete file"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * filesPerPage) + 1} to {Math.min(currentPage * filesPerPage, totalFiles)} of {totalFiles} files
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 border rounded-md text-sm ${
                            currentPage === pageNum
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <FilePreviewPane
        selectedFile={previewFile ? { fileName: previewFile.originalName, fileType: previewFile.fileType || '', fileSize: previewFile.fileSize } : null}
        previewUrl={previewUrl}
        previewLoading={previewLoading}
        onClose={() => { setPreviewFile(null); setPreviewUrl(null); }}
      />
      </div>

      {/* Edit File Modal */}
      {editingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Edit File</h2>
              <button
                onClick={() => setEditingFile(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File Name
                </label>
                <input
                  type="text"
                  value={editingFile.originalName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Enter file description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Tier
                  </label>
                  <select
                    value={editTier}
                    onChange={(e) => setEditTier(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={-1}>Admin Only</option>
                    <option value={0}>Tier 1 ($24)</option>
                    <option value={1}>Tier 2 ($50)</option>
                    <option value={2}>Tier 3 ($1000)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Folder (Optional)
                  </label>
                  <FolderSelector
                    folders={folders}
                    value={editFolder}
                    onChange={setEditFolder}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingFile(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Update File
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Delete File
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this file? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm({ show: false, fileId: null })}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


